import React, { useState, useEffect } from 'react';
import {
    Button,
    Typography,
    TextField,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { departamentoService } from '../../services/departamentoService';
import { municipioService } from '../../services/municipioService';
import { estratoService } from '../../services/estratoService';

const Paso2DatosFactura = ({ onNext, onBack, initialData }) => {
    const [valorFactura, setValorFactura] = useState(initialData.valorFactura || '');
    const [estrato, setEstrato] = useState(initialData.estrato || '');
    const [departamento, setDepartamento] = useState(initialData.departamento || '');
    const [municipio, setMunicipio] = useState(initialData.municipio || '');
    const [departamentos, setDepartamentos] = useState([]);
    const [municipios, setMunicipios] = useState([]);
    const [estratos, setEstratos] = useState([]);
    const [loadingDeptos, setLoadingDeptos] = useState(true);
    const [loadingMunicipios, setLoadingMunicipios] = useState(false);
    const [loadingEstratos, setLoadingEstratos] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    // Cargar datos iniciales
    useEffect(() => {
        const cargarDatosIniciales = async () => {
            try {
                const [deptos, estratosData] = await Promise.all([
                    departamentoService.obtenerTodos(),
                    estratoService.obtenerTodos()
                ]);
                setDepartamentos(deptos.data);
                setEstratos(estratosData.data);
            } catch (error) {
                enqueueSnackbar('Error al cargar datos iniciales', { variant: 'error' });
            } finally {
                setLoadingDeptos(false);
                setLoadingEstratos(false);
            }
        };

        cargarDatosIniciales();
    }, [enqueueSnackbar]);

    // Cargar municipios cuando cambia el departamento
    useEffect(() => {
        if (!departamento) {
            setMunicipios([]);
            setMunicipio('');
            return;
        }

        const cargarMunicipios = async () => {
            setLoadingMunicipios(true);
            try {
                const response = await municipioService.obtenerPorDepartamento(departamento.Id);
                setMunicipios(response.data);
            } catch (error) {
                enqueueSnackbar('Error al cargar municipios', { variant: 'error' });
            } finally {
                setLoadingMunicipios(false);
            }
        };

        cargarMunicipios();
    }, [departamento, enqueueSnackbar]);

    const handleContinue = () => {
        if (!valorFactura || !estrato || !departamento || !municipio) {
            enqueueSnackbar('Todos los campos son requeridos', { variant: 'error' });
            return;
        }

        const valorNumerico = parseFloat(valorFactura.replace(/[^0-9]/g, ''));

        if (valorNumerico < 1000000) {
            setOpenDialog(true);
        } else {
            proceedToNextStep();
        }
    };

    const proceedToNextStep = () => {
        onNext({
            valorFactura,
            estrato,
            departamento,
            municipio
        });
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0
        }).format(value);
    };

    const handleValorFacturaChange = (e) => {
        const rawValue = e.target.value.replace(/[^0-9]/g, '');
        const numericValue = rawValue ? parseInt(rawValue, 10) : '';
        setValorFactura(numericValue ? formatCurrency(numericValue) : '');
    };

    return (
        <div>
            <Typography variant="h5" gutterBottom>
                Información de tu factura eléctrica
            </Typography>

            <TextField
                fullWidth
                label="Valor mensual de tu factura"
                value={valorFactura}
                onChange={handleValorFacturaChange}
                margin="normal"
                helperText="Ingresa el valor total de tu última factura de energía"
            />

            <FormControl fullWidth margin="normal" disabled={loadingEstratos}>
                <InputLabel>Estrato</InputLabel>
                <Select
                    value={estrato}
                    onChange={(e) => setEstrato(e.target.value)}
                    label="Estrato"
                >
                    <MenuItem value="">
                        <em>Seleccione un estrato</em>
                    </MenuItem>
                    {estratos.map((est) => (
                        <MenuItem key={est.Id} value={est}>
                            {est.Nombre}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl fullWidth margin="normal" disabled={loadingDeptos}>
                <InputLabel>Departamento</InputLabel>
                <Select
                    value={departamento}
                    onChange={(e) => {
                        setDepartamento(e.target.value);
                        setMunicipio('');
                    }}
                    label="Departamento"
                >
                    <MenuItem value="">
                        <em>Seleccione un departamento</em>
                    </MenuItem>
                    {departamentos.map((depto) => (
                        <MenuItem key={depto.Id} value={depto}>
                            {depto.Nombre}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl fullWidth margin="normal" disabled={!departamento || loadingMunicipios}>
                <InputLabel>Ciudad/Municipio</InputLabel>
                <Select
                    value={municipio}
                    onChange={(e) => setMunicipio(e.target.value)}
                    label="Ciudad/Municipio"
                >
                    <MenuItem value="">
                        <em>{departamento ? 'Seleccione un municipio' : 'Primero seleccione un departamento'}</em>
                    </MenuItem>
                    {municipios.map((mun) => (
                        <MenuItem key={mun.Id} value={mun}>
                            {mun.Nombre}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                <Button onClick={onBack}>Atrás</Button>
                <Button variant="contained" onClick={handleContinue}>
                    Continuar
                </Button>
            </Box>

            <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
                <DialogTitle>Factura menor a $1.000.000</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Según nuestro análisis, con un consumo menor a $1.000.000 mensuales,
                        la instalación de paneles solares podría no ser viable económicamente.
                        ¿Deseas continuar con la precotización para ver un ejemplo de sostenibilidad?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>No, cancelar</Button>
                    <Button onClick={() => {
                        setOpenDialog(false);
                        proceedToNextStep();
                    }} autoFocus>
                        Sí, continuar
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default Paso2DatosFactura;